import { useState } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MoreHorizontal, CheckCircle, AlertTriangle, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Button } from "@/components/ui/button";
import { Mask } from "@/utils/mask";
import { ApiDataProps } from "@/pages/Tenants";
import { InvestigationSalesGrouped } from "@/Api/sales";
import { format, subHours } from "date-fns";
import { DetailModal } from "../DetailModal";

interface TypeTableProps {
  dataApi: ApiDataProps<InvestigationSalesGrouped>;
  pageActions: {
    next: () => Promise<void>;
    previous: () => Promise<void>;
    lastPage: () => Promise<void>;
    refresh: () => Promise<void>;
    silentRefresh: () => Promise<void>;
  };
  selectedStatus?: number;
}

export const TableData = ({
  dataApi,
  pageActions,
  selectedStatus = 1,
}: TypeTableProps) => {
  const [selectedContestId, setSelectedContestId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenDetails = (contestId: number) => {
    setSelectedContestId(contestId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedContestId(null);
  };

  const handleModalSuccess = async () => {
    await pageActions.silentRefresh();
  };

  return (
    <>
      <div className="rounded-md border">
        <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
          <TableHeader>
            <TableRow className="bg-blue-600 hover:bg-blue-600">
              <TableHead className="text-white font-bold">Loteria</TableHead>
              <TableHead className="text-white font-bold">Concurso</TableHead>
              <TableHead className="text-white font-bold">Data de Validade</TableHead>
              <TableHead className="text-white font-bold text-right">Total Pago Prêmios</TableHead>
              <TableHead className="text-white font-bold text-center">Bilhetes Apurados</TableHead>
              <TableHead className="text-white font-bold text-center">Data/Hora Apuração</TableHead>
              <TableHead className="text-white font-bold">Autor</TableHead>
              <TableHead className="text-white font-bold text-center">Ação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dataApi?.data && dataApi.data.length > 0 ? (
              dataApi.data.map((item) => {
                const isComplete = item.total_apurados === item.total_products;
                const hasPending = item.total_apurados < item.total_products;

                return (
                  <TableRow key={item.contest_id} className="hover:bg-zinc-100 dark:hover:bg-zinc-800">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: item.lottery_color || "#023859" }}
                        />
                        <span className="font-medium">{item.lottery_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{item.contest_name}</TableCell>
                    <TableCell>
                      {item.validation_date
                        ? format(new Date(item.validation_date + "T00:00:00"), "dd/MM/yyyy")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-green-600">
                      {Mask.formatCurrency(item.total_prize_paid || 0)}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span
                          className={cn(
                            "px-3 py-1 rounded-full text-sm font-medium",
                            isComplete
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                          )}
                        >
                          {item.total_apurados}/{item.total_products}
                        </span>
                        {isComplete ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : hasPending ? (
                          <AlertTriangle className="w-5 h-5 text-yellow-600" />
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {item.last_investigation_date
                        ? format(subHours(new Date(item.last_investigation_date.replace(" ", "T")), 3), "dd/MM/yyyy HH:mm")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {item.last_author_name || "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDetails(item.contest_id)}
                        className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                      >
                        <Eye className="size-5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                  Nenhuma apuração foi encontrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between items-center mt-4">
        {dataApi?.data?.length > 0 && (
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Página {dataApi?.current_page} de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <DetailModal
        contestId={selectedContestId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
        selectedStatus={selectedStatus}
      />
    </>
  );
};
